import {
  createContext,
  FC,
  FormEvent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'
import {
  getToken,
  graphQLMutate,
  graphQLQuery,
  handleGraphQLErrors,
  PHONE_LENGTH,
  phoneRegExp,
  phoneStringReplace,
  removePhoneRegExp,
  removePhoneStringReplace,
  sanitizeLoginUsername,
} from '../utils'
import { ICheckAccessToken, VALIDATE_TOKEN } from '../graphql/queries'
import { LOGIN, OAUTH_LOGIN } from '../graphql/mutations'
import { getEnvironmentName } from '../utils/token'
import {
  LOCAL_STORAGE_ERROR_EMAIL_OAUTH,
  SESSION_STORAGE_OAUTH_EMAIL,
  TUTOR_ROLE_ID,
} from '../utils/constants'

type LoginProviderProps = {
  children: ReactNode
}

interface IUser {
  login: string
  password: string
  username: string
  token: string
}

interface IOauthContent {
  description: string
  domain: string
  submit: string
}

interface IOauthLogin {
  data: {
    authenticationTypeEmail: {
      name: string
      url: string
      callback: string
    }
  }
}

type LoginContextType = {
  clearError: () => void
  error: string
  handleCloseModal: () => void
  handleOpenModal: () => void
  handleRegisterOptionClick: (url: string) => void
  loading: boolean
  modalOpen: boolean
  onSubmit: (values: any) => void
  user: IUser | null
  verifyOauthLogin: (username: string) => Promise<boolean | undefined>
  isOauthLogin: boolean
  loginWithOauth: (username: string) => void
  isCheckingOauth: boolean
  setIsOauthLogin: (value: boolean) => void
  isRedirectingOauth: boolean
  handleSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>
  isSubmitting: boolean
  onPasswordChange: (value: string) => void
  onUserNameChange: (value: string) => void
  phoneMask: RegExp | null
  recoverPasswordLink: string
  onUsernameBlur: () => void
  onLoginWithOauthClick: () => void
  username: string
  goBackToLogin: () => void
  inputErrors: { username: boolean; password: boolean }
  oauthContent: IOauthContent | null
}

const LoginContext = createContext({} as LoginContextType)

const LoginProvider: FC<LoginProviderProps> = ({ children }) => {
  const [error, setError] = useState('')
  const [user, setUser] = useState<IUser | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isOauthLogin, setIsOauthLogin] = useState(false)
  const [isCheckingOauth, setIsCheckingOauth] = useState(false)
  const [isRedirectingOauth, setIsRedirectingOauth] = useState(false)
  const [username, setUsername] = useState('')
  const actualUsernameRef = useRef(username)
  const [password, setPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [phoneMask, setPhoneMask] = useState<null | RegExp>(null)
  const [inputErrors, setInputErrors] = useState({ username: false, password: false })
  const [oauthContent, setOauthContent] = useState<IOauthContent | null>(null)

  const navigate = useNavigate()

  const handleRegisterOptionClick = useCallback((url: string) => {
    window.location.href = url
  }, [])

  const verifyOauthLogin = useCallback(async (username: string): Promise<boolean | undefined> => {
    const isAnEmail = username.includes('@')

    if (!isAnEmail) {
      return
    }

    const userNameDomain = username.split('@')[1]

    try {
      setIsCheckingOauth(true)
      const response = await fetch(
        `${
          process.env.REACT_APP_ALLOWED_OAUTH_DOMAINS
        }${getEnvironmentName()}.json?v=${new Date().getTime()}`,
      )

      const data = await response.json()

      const hasOauth = data?.google?.includes(userNameDomain)

      const oauthContent = data?.providers?.google?.find((oauthContent: IOauthContent) =>
        oauthContent.domain.includes(userNameDomain),
      )

      setOauthContent(oauthContent)

      setIsOauthLogin(hasOauth)

      return hasOauth
    } catch (error) {
      console.warn(error)
    } finally {
      setIsCheckingOauth(false)
    }
  }, [])

  const loginWithOauth = useCallback(
    async (username: string) => {
      if (!isOauthLogin) {
        return
      }

      try {
        setIsRedirectingOauth(true)
        const {
          data: {
            authenticationTypeEmail: { url },
          },
        }: IOauthLogin = await graphQLMutate(OAUTH_LOGIN, {
          variables: {
            email: username,
          },
        })

        sessionStorage.setItem(SESSION_STORAGE_OAUTH_EMAIL, username)

        window.location.href = `${url}&login_hint=${username}`

        return
      } catch (error: any) {
        if (error?.graphQLErrors) {
          const errorData = handleGraphQLErrors(error.graphQLErrors)[0]

          setError(errorData)
          return
        }

        setError('Ocorreu um erro ao realizar o login com o @EDU, tente novamente mais tarde.')
      } finally {
        setIsRedirectingOauth(false)
      }
    },
    [isOauthLogin],
  )

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const isEmail = username.includes('@')

    if (isEmail) {
      const isOauth = await verifyOauthLogin(username)

      if (isOauth) {
        return
      }
    }

    if (!username || !password) {
      setInputErrors({ username: !username, password: !password })
      return
    }

    await onSubmit({ username, password })
  }

  const onUserNameChange = async (value: string): Promise<void> => {
    const formattedValue = value.trim()

    if (phoneMask === phoneRegExp) {
      setPhoneMask(removePhoneRegExp)
      setUsername(formattedValue.replace(removePhoneRegExp, removePhoneStringReplace))
      return
    }

    if (formattedValue.length === PHONE_LENGTH && !Number.isNaN(Number(formattedValue))) {
      setPhoneMask(phoneRegExp)
      setUsername(formattedValue.replace(phoneRegExp, phoneStringReplace))
      return
    }

    setPhoneMask(null)
    setUsername(formattedValue)
    actualUsernameRef.current = formattedValue
  }

  const onPasswordChange = (value: string) => setPassword(value.trim())

  const recoverPasswordLink = `${process.env.REACT_APP_SOMOS_ONBOARDING_URL}/conta/esqueci-minha-senha`

  const onUsernameBlur = async () => {
    if (!actualUsernameRef.current) {
      setIsOauthLogin(false)
      return
    }

    await verifyOauthLogin(actualUsernameRef.current)
  }

  const onLoginWithOauthClick = () => loginWithOauth(username)

  const goBackToLogin = () => setIsOauthLogin(false)

  useEffect(() => {
    const validateToken = async () => {
      const { REACT_APP_PLURALL_TUTORING_URL, REACT_APP_PLURALL_HOME_URL } = process.env

      if (!REACT_APP_PLURALL_HOME_URL || !REACT_APP_PLURALL_TUTORING_URL) {
        console.error('Missing environment variables')

        navigate('/logout')
        return
      }

      setLoading(true)
      try {
        const {
          error,
          data: {
            checkAccessToken: {
              person: { tutors, roles },
            },
          },
        }: ICheckAccessToken = await graphQLQuery(VALIDATE_TOKEN, {
          fetchPolicy: 'network-only',
          variables: {
            accessToken: getToken(),
          },
        })

        if (error) {
          navigate('/logout')
          return
        }

        const isOnlyTutor = roles.every(role => role.id === TUTOR_ROLE_ID)
        const hasActiveTutorPosition = Boolean(tutors.length)

        if (isOnlyTutor && hasActiveTutorPosition) {
          window.location.href = REACT_APP_PLURALL_TUTORING_URL
          return
        }

        window.location.href = REACT_APP_PLURALL_HOME_URL
      } catch (e) {
        console.error(e)

        navigate('/logout')
      }
    }

    const getOauthErrorCallback = async () => {
      const errorOauthStorage = localStorage?.getItem(LOCAL_STORAGE_ERROR_EMAIL_OAUTH)

      const errorOauth = errorOauthStorage ? JSON.parse(errorOauthStorage) : null

      if (errorOauth) {
        localStorage?.removeItem(LOCAL_STORAGE_ERROR_EMAIL_OAUTH)
        setError(errorOauth.error)
        setIsOauthLogin(true)
        await onUserNameChange(errorOauth.email)
        await onUsernameBlur()
      }
    }

    getOauthErrorCallback()

    if (getToken()) {
      validateToken()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenModal = useCallback(() => setModalOpen(true), [])

  const handleCloseModal = useCallback(() => setModalOpen(false), [])

  const clearError = useCallback(() => setError(''), [])

  const onSubmit = useCallback(
    async ({ ...values }) => {
      setIsSubmitting(true)
      setInputErrors({ username: false, password: false })
      const login = sanitizeLoginUsername(values.username)

      try {
        clearError()

        const { data } = await graphQLMutate(LOGIN, {
          variables: {
            username: login,
            password: values.password,
          },
        })

        const userData = { login, ...values, ...data.login }

        setUser(userData)
      } catch (error: any) {
        if (error?.graphQLErrors) {
          const errorData = handleGraphQLErrors(error.graphQLErrors)[0]

          setError(errorData)
          return
        }

        setError('Ocorreu um erro ao realizar o login, tente novamente mais tarde.')
      } finally {
        setIsSubmitting(false)
      }
    },
    [clearError],
  )

  return (
    <LoginContext.Provider
      value={{
        clearError,
        error,
        handleCloseModal,
        handleOpenModal,
        handleRegisterOptionClick,
        loading,
        modalOpen,
        onSubmit,
        user,
        verifyOauthLogin,
        isOauthLogin,
        loginWithOauth,
        isCheckingOauth,
        setIsOauthLogin,
        isRedirectingOauth,
        handleSubmit,
        isSubmitting,
        onPasswordChange,
        onUserNameChange,
        phoneMask,
        recoverPasswordLink,
        onUsernameBlur,
        onLoginWithOauthClick,
        username,
        goBackToLogin,
        inputErrors,
        oauthContent,
      }}
    >
      {children}
    </LoginContext.Provider>
  )
}

export { LoginContext, LoginProvider }
